import { BarChart } from '@mantine/charts';
import { Badge, Box, Group, Paper, Table, Text } from '@mantine/core';

import { BASE_LABELS } from '../../constants/base';
import { analyticsTypes } from '../../types';
import {
  formatCompactAxis,
  formatMoney,
  formatNumber,
  formatPeriodCaption,
  formatShare,
  getNiceAxisMax,
  toPeriodDates,
} from '../../utils/base';

import { COLORS } from '@/utils/colors';

type ProductsSectionProps = {
  report: analyticsTypes.BaseAnalyticsReport;
};

export const ProductsSection = ({ report }: ProductsSectionProps) => {
  const products = report.products;
  const currency = report.currency;
  const chartHeight = Math.max(180, products.length * 36);
  const revenueScaleMax = Math.max(0, ...products.map((row) => row.revenue));
  const revenueAxisMax = getNiceAxisMax(revenueScaleMax);
  const formatRevenueAxis = (value: number) => formatCompactAxis(Number(value), revenueAxisMax);

  return (
    <Box style={{ minWidth: 0, maxWidth: '100%' }}>
      <Group justify="space-between" mb="sm" wrap="wrap">
        <Text fw={600}>{BASE_LABELS.PRODUCTS}</Text>
        <Text size="sm" c="dimmed">
          {formatPeriodCaption(...toPeriodDates(report.period))}
        </Text>
      </Group>

      {products.length === 0 ? (
        <Paper p="md" radius="md" withBorder bg={COLORS.cardBackground}>
          <Text c="dimmed">{BASE_LABELS.EMPTY_PRODUCTS}</Text>
        </Paper>
      ) : (
        <>
          <Paper
            p="md"
            radius="md"
            withBorder
            mb="md"
            bg={COLORS.cardBackground}
            style={{ overflow: 'hidden', maxWidth: '100%' }}
          >
            <BarChart
              h={chartHeight}
              data={products.map((row) => ({
                product: row.product,
                work: row.category === 'work' ? row.revenue : 0,
                addon: row.category === 'addon' ? row.revenue : 0,
                other: row.category === 'other' ? row.revenue : 0,
                share: row.share,
              }))}
              dataKey="product"
              orientation="vertical"
              type="stacked"
              series={[
                { name: 'work', color: 'teal.5', label: BASE_LABELS.WORK },
                { name: 'addon', color: 'violet.5', label: BASE_LABELS.ADDON },
                { name: 'other', color: 'gray.5', label: BASE_LABELS.OTHER },
              ]}
              withLegend
              withTooltip
              tickLine="y"
              gridAxis="x"
              // Mantine wires valueFormatter to axis ticks too — only format numeric values.
              valueFormatter={(value) =>
                typeof value === 'number' && Number.isFinite(value)
                  ? formatMoney(value, currency)
                  : String(value)
              }
              yAxisProps={{
                width: 140,
                // Recharts skips every other category tick when height is tight
                // (exactly 5 products → 2nd/4th labels missing). Force all names.
                interval: 0,
                tickFormatter: (value) => String(value),
              }}
              xAxisProps={{
                domain: [0, revenueAxisMax],
                tickCount: 5,
                tickFormatter: formatRevenueAxis,
                tickMargin: 8,
              }}
              tooltipProps={{
                content: ({ label, payload }) => {
                  const row = payload?.[0]?.payload as
                    | { work?: number; addon?: number; other?: number; share?: number }
                    | undefined;
                  if (!row) {
                    return null;
                  }
                  const sum = (row.work || 0) + (row.addon || 0) + (row.other || 0);
                  return (
                    <Paper p="xs" withBorder bg={COLORS.accentBackground}>
                      <Text size="sm">{String(label)}</Text>
                      <Text size="sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatMoney(sum, currency)} ({formatShare(row.share || 0)})
                      </Text>
                    </Paper>
                  );
                },
              }}
            />
          </Paper>

          <Paper
            p="md"
            radius="md"
            withBorder
            bg={COLORS.cardBackground}
            style={{ overflow: 'hidden' }}
          >
            <Box style={{ overflowX: 'auto', maxWidth: '100%' }}>
              <Table highlightOnHover horizontalSpacing="md" verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{BASE_LABELS.PRODUCT}</Table.Th>
                    <Table.Th>{BASE_LABELS.CATEGORY}</Table.Th>
                    <Table.Th ta="right">{BASE_LABELS.CHECKS}</Table.Th>
                    <Table.Th ta="right">{BASE_LABELS.REVENUE}</Table.Th>
                    <Table.Th ta="right">{BASE_LABELS.AVG_CHECK}</Table.Th>
                    <Table.Th ta="right">{BASE_LABELS.SHARE}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {products.map((row) => (
                    <Table.Tr key={row.product}>
                      <Table.Td>
                        <ProductCell name={row.product} share={row.share} category={row.category} />
                      </Table.Td>
                      <Table.Td>
                        <Badge
                          variant="outline"
                          color={
                            row.category === 'work'
                              ? 'teal'
                              : row.category === 'addon'
                                ? 'violet'
                                : 'gray'
                          }
                          radius="xl"
                        >
                          {row.category === 'work'
                            ? BASE_LABELS.WORK
                            : row.category === 'addon'
                              ? BASE_LABELS.ADDON
                              : BASE_LABELS.OTHER}
                        </Badge>
                      </Table.Td>
                      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatNumber(row.checks)}
                      </Table.Td>
                      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatMoney(row.revenue, currency)}
                      </Table.Td>
                      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatMoney(row.averageCheck, currency)}
                      </Table.Td>
                      <Table.Td ta="right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatShare(row.share)}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          </Paper>
        </>
      )}
    </Box>
  );
};

const ProductCell = ({
  name,
  share,
  category,
}: {
  name: string;
  share: number;
  category: analyticsTypes.BaseAnalyticsProductRow['category'];
}) => (
  <Box
    pos="relative"
    style={{ overflow: 'hidden', borderRadius: 6, minWidth: 120, whiteSpace: 'nowrap' }}
  >
    <Box
      pos="absolute"
      top={0}
      left={0}
      h="100%"
      w={`${Math.max(share * 100, 2)}%`}
      style={{
        backgroundColor:
          category === 'other' ? 'rgba(134, 142, 150, 0.18)' : 'rgba(45, 212, 191, 0.18)',
        borderRadius: 6,
      }}
    />
    <Text pos="relative" size="sm" px={8} py={6}>
      {name}
    </Text>
  </Box>
);
